import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { useSEO } from "@/lib/useSEO";
import { getTerm } from "@/data/glossary";
import { ArrowRight, ArrowLeft, Check, X } from "lucide-react";

const SITE = "https://betterthroughboxing.com";

export default function GlossaryTerm() {
  const { slug } = useParams();
  const t = getTerm(slug);

  useSEO({
    title: t ? `${t.term} — How to & Common Mistakes | Boxing Glossary` : "Boxing Glossary",
    description: t ? `${t.term}: ${t.short} Learn how to do it step by step and the mistakes to avoid, from Coach Chris.` : "",
    path: `/boxing-glossary/${slug}`,
    keywords: t ? [`boxing ${t.term.toLowerCase()}`, `how to ${t.term.toLowerCase()}`, "boxing technique", "boxing for beginners"] : [],
  });

  useEffect(() => {
    if (!t) return;
    const ld = {
      "@context": "https://schema.org", "@type": "HowTo",
      name: `How to throw a ${t.term} in boxing`,
      description: t.definition,
      step: t.howTo.map((s, i) => ({ "@type": "HowToStep", position: i + 1, text: s })),
      url: `${SITE}/boxing-glossary/${t.slug}`,
    };
    const el = document.createElement("script");
    el.type = "application/ld+json"; el.id = "glossary-jsonld"; el.text = JSON.stringify(ld);
    document.getElementById("glossary-jsonld")?.remove();
    document.head.appendChild(el);
    return () => { document.getElementById("glossary-jsonld")?.remove(); };
  }, [t]);

  if (!t) return <Navigate to="/boxing-glossary" replace />;
  const related = (t.related || []).map(getTerm).filter(Boolean);

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <SiteHeader />
      <article className="max-w-3xl mx-auto px-6 pt-16 pb-16">
        <Link to="/boxing-glossary" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-zinc-500 hover:text-white mb-6">
          <ArrowLeft className="h-3.5 w-3.5" /> Boxing glossary
        </Link>
        <p className="text-[11px] uppercase tracking-[0.3em] text-[#FF331F] font-bold">{t.category}</p>
        <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight mt-3">{t.term}</h1>
        <p className="text-lg text-zinc-300 mt-4">{t.definition}</p>

        <h2 className="font-display text-2xl uppercase tracking-tight mt-10 mb-4">How to do it</h2>
        <ol className="space-y-3">
          {t.howTo.map((s, i) => (
            <li key={i} className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-[#FF331F] text-white text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
              <span className="text-zinc-200">{s}</span>
            </li>
          ))}
        </ol>

        <h2 className="font-display text-2xl uppercase tracking-tight mt-10 mb-4">Common mistakes</h2>
        <ul className="space-y-2">
          {t.mistakes.map((m, i) => (
            <li key={i} className="flex items-start gap-2 text-zinc-300"><X className="h-4 w-4 text-red-500 mt-0.5 shrink-0" /> {m}</li>
          ))}
        </ul>

        <div className="border border-[#27272A] bg-[#0F0F0F] p-6 mt-12 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-zinc-200"><Check className="h-5 w-5 text-[#FF331F]" /> Want Coach Chris to drill this with you?</div>
          <Link to="/book" className="inline-flex items-center gap-2 bg-[#FF331F] hover:bg-[#FF5242] text-white uppercase tracking-widest font-bold text-xs px-5 py-3" data-testid="glossary-book-cta">
            Book a session <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        {related.length > 0 && (
          <div className="mt-10">
            <div className="text-xs uppercase tracking-widest text-zinc-500 mb-3">Related</div>
            <div className="flex flex-wrap gap-2">
              {related.map((r) => (
                <Link key={r.slug} to={`/boxing-glossary/${r.slug}`} className="text-xs border border-[#27272A] px-3 py-1.5 text-zinc-400 hover:border-[#FF331F] hover:text-white">{r.term}</Link>
              ))}
            </div>
          </div>
        )}
      </article>
      <SiteFooter />
    </div>
  );
}
